/**
 * Supabase Client for Frontend
 * 
 * This module provides a configured Supabase client for use in React components.
 * It uses the anon key which respects Row Level Security (RLS) policies.
 * 
 * IMPORTANT: The user must be authenticated via Firebase before accessing Supabase data.
 * We bridge Firebase Auth to Supabase by passing the Firebase UID in queries.
 */

import { createClient } from '@supabase/supabase-js';
import { getSupabaseAccessToken, isRlsBridgeEnabled } from './supabaseToken';

// Get credentials from environment variables
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn(
    'Supabase credentials not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env'
  );
}

/**
 * Supabase Client Instance
 * 
 * Uses anon key - respects Row Level Security.
 * User's Firebase UID is passed via query filters (owner_id = uid).
 *
 * RLS bridge (multi-owner Phase 7): when the build sets
 * REACT_APP_SUPABASE_RLS_BRIDGE=true, every request also carries a
 * Supabase-signed JWT with the Firebase identity (minted by the
 * getSupabaseToken callable), activating the JWT-scoped RLS policies.
 * Returning null from the callback falls back to the anon key, so a mint
 * failure degrades to exactly the pre-bridge behavior.
 */
export const supabase = SUPABASE_URL && SUPABASE_ANON_KEY
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
      ...(isRlsBridgeEnabled()
        ? { accessToken: () => getSupabaseAccessToken() }
        : {}),
    })
  : null;

/**
 * Check if Supabase is properly configured
 */
export function isSupabaseConfigured() {
  return supabase !== null;
}

/**
 * Get the Supabase client, throwing if not configured
 */
export function getSupabase() {
  if (!supabase) {
    throw new Error(
      'Supabase is not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.'
    );
  }
  return supabase;
}

/**
 * Transform snake_case Supabase rows to camelCase for frontend compatibility
 * This allows existing components to work without changes.
 */
export function transformContactToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    ownerId: row.owner_id,
    // Multi-owner visibility (read-only mirrors; shared_with_ids is maintained
    // by DB triggers from crm_record_shares — the app must never write it)
    recordScope: row.record_scope || 'user',
    sharedWithIds: row.shared_with_ids || [],
    // Core Identity
    firstName: row.first_name,
    lastName: row.last_name,
    middleInitial: row.middle_initial,
    email: row.email,
    phone: row.phone,
    secondaryEmail: row.secondary_email,
    secondaryPhone: row.secondary_phone,
    birthday: row.birthday,
    // Professional Details
    company: row.company,
    jobTitle: row.job_title,
    licenseNumber: row.license_number,
    licenseState: row.license_state,
    website: row.website,
    officeAddress: row.office_address,
    preferredContactMethod: row.preferred_contact_method,
    // Status
    isActive: row.is_active,
    isFavorite: row.is_favorite,
    dnc: row.do_not_contact, // master / "Everything" DNC
    dncCall: row.dnc_call,
    dncEmail: row.dnc_email,
    dncSms: row.dnc_sms,
    // Classification
    source: row.source,
    contactTypes: row.contact_types || [],
    primaryContactType: row.primary_contact_type,
    tags: row.tags || [],
    // Read-only mirror of active campaign template ids (maintained by DB
    // triggers). Deliberately absent from transformContactToSnakeCase: the
    // app must never write it.
    activeCampaignIds: row.active_campaign_ids || [],
    // Mailing Address (contact's residence, NOT deal property)
    mailingAddress: {
      street: row.mailing_street,
      city: row.mailing_city,
      state: row.mailing_state,
      zip: row.mailing_zip,
    },
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    // RETR Recruiting Intelligence — Core (from initial migration)
    retrProfileUrl: row.retr_profile_url,
    retrLoType: row.retr_lo_type,
    retrState: row.retr_state,
    retrLoanCount: row.retr_loan_count,
    retrLoanVolume: row.retr_loan_volume,
    retrConvCount: row.retr_conv_count,
    retrVaCount: row.retr_va_count,
    retrFhaCount: row.retr_fha_count,
    retrTotalLoanCount: row.retr_total_loan_count,
    retrTotalLoanVolume: row.retr_total_loan_volume,
    retrAvgLoan: row.retr_avg_loan,
    retrLocation: row.retr_location,
    retrImportedAt: row.retr_imported_at,
    // RETR — Identity & Licensing
    retrProfileId: row.retr_profile_id,
    retrNmlsId: row.retr_nmls_id,
    retrLicensedSince: row.retr_licensed_since,
    retrJobTitle: row.retr_job_title,
    // RETR — Contact Info
    retrWorkEmail: row.retr_work_email,
    retrPersonalEmail: row.retr_personal_email,
    retrOfficePhone: row.retr_office_phone,
    retrMobilePhone: row.retr_mobile_phone,
    // RETR — Social / Web Presence
    retrLinkedinUrl: row.retr_linkedin_url,
    retrFacebookUrl: row.retr_facebook_url,
    retrTwitterUrl: row.retr_twitter_url,
    retrInstagramUrl: row.retr_instagram_url,
    retrZillowUrl: row.retr_zillow_url,
    retrWebsiteUrl: row.retr_website_url,
    retrExperienceUrl: row.retr_experience_url,
    // RETR — Search Links
    retrGoogleSearchUrl: row.retr_google_search_url,
    retrFacebookSearchUrl: row.retr_facebook_search_url,
    retrLinkedinSearchUrl: row.retr_linkedin_search_url,
    retrZillowSearchUrl: row.retr_zillow_search_url,
    retrNmlsLookupUrl: row.retr_nmls_lookup_url,
    // RETR — Current Employer
    retrCompanyName: row.retr_company_name,
    retrBranchUrl: row.retr_branch_url,
    retrOfficeLocation: row.retr_office_location,
    retrEmployerStartDate: row.retr_employer_start_date,
    // RETR — Summary Stats
    retrBuyerAgentsCount: row.retr_buyer_agents_count,
    retrYearsExperience: row.retr_years_experience,
    retrStateLicensesCount: row.retr_state_licenses_count,
    retrFederalRegistrationsCount: row.retr_federal_registrations_count,
    // RETR — Metadata
    retrTrackRecordKey: row.retr_track_record_key,
    retrHasPersonality: row.retr_has_personality,
    // RETR — JSONB (nested/array data)
    retrLicenses: row.retr_licenses,
    retrEmploymentHistory: row.retr_employment_history,
    retrBranches: row.retr_branches,
    retrAnalytics: row.retr_analytics,
    retrPersonality: row.retr_personality,
    // Extra fields (unmapped CSV data)
    extraFields: row.extra_fields || null,
    // Stats from the view_contact_summary (calculated dynamically)
    stats: {
      totalDeals: row.total_deals || 0,
      wonDeals: row.won_deals || 0,
      lifetimeValue: row.lifetime_value || 0,
      lastContactDate: row.last_interaction_at,
    },
  };
}

/**
 * Transform camelCase frontend data to snake_case for Supabase inserts/updates
 */
export function transformContactToSnakeCase(data) {
  const row = {};
  
  if (data.ownerId !== undefined) row.owner_id = data.ownerId;
  // Core Identity
  if (data.firstName !== undefined) row.first_name = data.firstName;
  if (data.lastName !== undefined) row.last_name = data.lastName;
  if (data.middleInitial !== undefined) row.middle_initial = data.middleInitial;
  if (data.email !== undefined) row.email = data.email ? data.email.toLowerCase() : null;
  if (data.phone !== undefined) row.phone = data.phone;
  if (data.secondaryEmail !== undefined) row.secondary_email = data.secondaryEmail;
  if (data.secondaryPhone !== undefined) row.secondary_phone = data.secondaryPhone;
  if (data.birthday !== undefined) row.birthday = data.birthday;
  // Professional Details
  if (data.company !== undefined) row.company = data.company;
  if (data.jobTitle !== undefined) row.job_title = data.jobTitle;
  if (data.licenseNumber !== undefined) row.license_number = data.licenseNumber;
  if (data.licenseState !== undefined) row.license_state = data.licenseState;
  if (data.website !== undefined) row.website = data.website;
  if (data.officeAddress !== undefined) row.office_address = data.officeAddress;
  if (data.preferredContactMethod !== undefined) row.preferred_contact_method = data.preferredContactMethod;
  // Status
  if (data.isActive !== undefined) row.is_active = data.isActive;
  if (data.isFavorite !== undefined) row.is_favorite = data.isFavorite;
  if (data.dnc !== undefined) row.do_not_contact = data.dnc;
  if (data.dncCall !== undefined) row.dnc_call = data.dncCall;
  if (data.dncEmail !== undefined) row.dnc_email = data.dncEmail;
  if (data.dncSms !== undefined) row.dnc_sms = data.dncSms;
  // Classification
  if (data.source !== undefined) row.source = data.source;
  if (data.contactTypes !== undefined) row.contact_types = data.contactTypes;
  if (data.primaryContactType !== undefined) row.primary_contact_type = data.primaryContactType;
  if (data.tags !== undefined) row.tags = data.tags;
  
  // Mailing Address (contact's residence, NOT deal property)
  if (data.mailingAddress) {
    row.mailing_street = data.mailingAddress.street || null;
    row.mailing_city = data.mailingAddress.city || null;
    row.mailing_state = data.mailingAddress.state || null;
    row.mailing_zip = data.mailingAddress.zip || null;
  }

  // RETR Recruiting Intelligence — Core
  if (data.retrProfileUrl !== undefined) row.retr_profile_url = data.retrProfileUrl || null;
  if (data.retrLoType !== undefined) row.retr_lo_type = data.retrLoType || null;
  if (data.retrState !== undefined) row.retr_state = data.retrState || null;
  if (data.retrLoanCount !== undefined) row.retr_loan_count = data.retrLoanCount || null;
  if (data.retrLoanVolume !== undefined) row.retr_loan_volume = data.retrLoanVolume || null;
  if (data.retrConvCount !== undefined) row.retr_conv_count = data.retrConvCount || null;
  if (data.retrVaCount !== undefined) row.retr_va_count = data.retrVaCount || null;
  if (data.retrFhaCount !== undefined) row.retr_fha_count = data.retrFhaCount || null;
  if (data.retrTotalLoanCount !== undefined) row.retr_total_loan_count = data.retrTotalLoanCount || null;
  if (data.retrTotalLoanVolume !== undefined) row.retr_total_loan_volume = data.retrTotalLoanVolume || null;
  if (data.retrAvgLoan !== undefined) row.retr_avg_loan = data.retrAvgLoan || null;
  if (data.retrLocation !== undefined) row.retr_location = data.retrLocation || null;
  if (data.retrImportedAt !== undefined) row.retr_imported_at = data.retrImportedAt || null;
  // RETR — Identity & Licensing
  if (data.retrProfileId !== undefined) row.retr_profile_id = data.retrProfileId || null;
  if (data.retrNmlsId !== undefined) row.retr_nmls_id = data.retrNmlsId || null;
  if (data.retrLicensedSince !== undefined) row.retr_licensed_since = data.retrLicensedSince || null;
  if (data.retrJobTitle !== undefined) row.retr_job_title = data.retrJobTitle || null;
  // RETR — Contact Info
  if (data.retrWorkEmail !== undefined) row.retr_work_email = data.retrWorkEmail || null;
  if (data.retrPersonalEmail !== undefined) row.retr_personal_email = data.retrPersonalEmail || null;
  if (data.retrOfficePhone !== undefined) row.retr_office_phone = data.retrOfficePhone || null;
  if (data.retrMobilePhone !== undefined) row.retr_mobile_phone = data.retrMobilePhone || null;
  // RETR — Social / Web Presence
  if (data.retrLinkedinUrl !== undefined) row.retr_linkedin_url = data.retrLinkedinUrl || null;
  if (data.retrFacebookUrl !== undefined) row.retr_facebook_url = data.retrFacebookUrl || null;
  if (data.retrTwitterUrl !== undefined) row.retr_twitter_url = data.retrTwitterUrl || null;
  if (data.retrInstagramUrl !== undefined) row.retr_instagram_url = data.retrInstagramUrl || null;
  if (data.retrZillowUrl !== undefined) row.retr_zillow_url = data.retrZillowUrl || null;
  if (data.retrWebsiteUrl !== undefined) row.retr_website_url = data.retrWebsiteUrl || null;
  if (data.retrExperienceUrl !== undefined) row.retr_experience_url = data.retrExperienceUrl || null;
  // RETR — Search Links
  if (data.retrGoogleSearchUrl !== undefined) row.retr_google_search_url = data.retrGoogleSearchUrl || null;
  if (data.retrFacebookSearchUrl !== undefined) row.retr_facebook_search_url = data.retrFacebookSearchUrl || null;
  if (data.retrLinkedinSearchUrl !== undefined) row.retr_linkedin_search_url = data.retrLinkedinSearchUrl || null;
  if (data.retrZillowSearchUrl !== undefined) row.retr_zillow_search_url = data.retrZillowSearchUrl || null;
  if (data.retrNmlsLookupUrl !== undefined) row.retr_nmls_lookup_url = data.retrNmlsLookupUrl || null;
  // RETR — Current Employer
  if (data.retrCompanyName !== undefined) row.retr_company_name = data.retrCompanyName || null;
  if (data.retrBranchUrl !== undefined) row.retr_branch_url = data.retrBranchUrl || null;
  if (data.retrOfficeLocation !== undefined) row.retr_office_location = data.retrOfficeLocation || null;
  if (data.retrEmployerStartDate !== undefined) row.retr_employer_start_date = data.retrEmployerStartDate || null;
  // RETR — Summary Stats
  if (data.retrBuyerAgentsCount !== undefined) row.retr_buyer_agents_count = data.retrBuyerAgentsCount || null;
  if (data.retrYearsExperience !== undefined) row.retr_years_experience = data.retrYearsExperience || null;
  if (data.retrStateLicensesCount !== undefined) row.retr_state_licenses_count = data.retrStateLicensesCount || null;
  if (data.retrFederalRegistrationsCount !== undefined) row.retr_federal_registrations_count = data.retrFederalRegistrationsCount || null;
  // RETR — Metadata
  if (data.retrTrackRecordKey !== undefined) row.retr_track_record_key = data.retrTrackRecordKey || null;
  if (data.retrHasPersonality !== undefined) row.retr_has_personality = data.retrHasPersonality ?? null;
  // RETR — JSONB
  if (data.retrLicenses !== undefined) row.retr_licenses = data.retrLicenses || null;
  if (data.retrEmploymentHistory !== undefined) row.retr_employment_history = data.retrEmploymentHistory || null;
  if (data.retrBranches !== undefined) row.retr_branches = data.retrBranches || null;
  if (data.retrAnalytics !== undefined) row.retr_analytics = data.retrAnalytics || null;
  if (data.retrPersonality !== undefined) row.retr_personality = data.retrPersonality || null;
  if (data.extraFields !== undefined) row.extra_fields = data.extraFields || null;
  
  return row;
}

/**
 * Transform snake_case deal rows to camelCase for frontend compatibility
 */
export function transformDealToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    contactId: row.contact_id,
    ownerId: row.owner_id,
    // Multi-owner visibility (read-only mirrors; shared_with_ids is maintained
    // by DB triggers from crm_record_shares — the app must never write it)
    recordScope: row.record_scope || 'user',
    sharedWithIds: row.shared_with_ids || [],
    dealName: row.deal_name,
    stage: row.stage,
    amount: row.amount,
    product: row.product,
    estimatedClosingDate: row.estimated_closing_date,
    appDate: row.app_date,
    // Loan details
    loanNumber: row.loan_number,
    lender: row.lender,
    loanAmount: row.loan_amount,
    downPayment: row.down_payment,
    interestRate: row.interest_rate,
    loanTerm: row.loan_term,
    // Tags
    tags: row.tags || [],
    // Read-only mirror of the linked contact's active campaign template ids
    // (maintained by DB triggers). Deliberately absent from
    // transformDealToSnakeCase: the app must never write it.
    activeCampaignIds: row.active_campaign_ids || [],
    // Read-only mirror of the DISTINCT crm_deal_participants.role values on
    // this deal (maintained by DB trigger). Never write it.
    dealTeamRoles: row.deal_team_roles || [],
    // Reconstruct nested propertyAddress for backward compatibility
    propertyAddress: {
      street: row.property_street,
      city: row.property_city,
      state: row.property_state,
      zip: row.property_zip,
    },
    propertyType: row.property_type,
    occupancyType: row.occupancy_type || 'primary_residence', // primary_residence, second_home, investment
    // Reconstruct nested buyerAgent for backward compatibility
    buyerAgent: {
      name: row.buyer_agent_name,
      email: row.buyer_agent_email,
      phone: row.buyer_agent_phone,
      company: row.buyer_agent_company,
    },
    // Reconstruct nested sellerAgent for backward compatibility
    sellerAgent: {
      name: row.seller_agent_name,
      email: row.seller_agent_email,
      phone: row.seller_agent_phone,
      company: row.seller_agent_company,
    },
    // Reconstruct nested titleCompany for backward compatibility
    titleCompany: {
      name: row.title_company_name,
      contact: row.title_company_contact,
      email: row.title_company_email,
      phone: row.title_company_phone,
    },
    loanPurpose: row.loan_purpose,
    purchaseOrRefi: row.purchase_or_refi,
    creditScore: row.credit_score,
    // Reconstruct nested sourceDetails for backward compatibility
    sourceDetails: {
      campaignId: row.source_campaign,
      medium: row.source_medium,
      term: row.source_term,
    },
    source: row.source_medium,
    formName: row.form_name,
    nextAction: row.next_action,
    nextActionDue: row.next_action_due,
    isAtRisk: row.is_at_risk,
    ariveId: row.arive_id,
    ariveSysGuid: row.arive_sys_guid,
    ariveDeepLink: row.arive_deep_link,
    ariveEntityType: row.arive_entity_type,
    ariveLastSyncedAt: row.arive_last_synced_at,
    ariveLoanData: row.arive_loan_data || {},
    slaTimestamp: row.sla_timestamp,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    extraFields: row.extra_fields || null,
    // DNC mirrors (read-only; denormalized from the linked contact by DB triggers)
    dnc: row.dnc,
    dncCall: row.dnc_call,
    dncEmail: row.dnc_email,
    dncSms: row.dnc_sms,
    // Include related data if joined
    contact: row.contact ? transformContactToCamelCase(row.contact) : null,
    relatedParties: row.participants || [],
  };
}

/**
 * Transform camelCase deal data to snake_case for Supabase inserts/updates
 */
export function transformDealToSnakeCase(data) {
  const row = {};
  
  if (data.contactId !== undefined) row.contact_id = data.contactId;
  if (data.ownerId !== undefined) row.owner_id = data.ownerId;
  if (data.dealName !== undefined) row.deal_name = data.dealName;
  if (data.stage !== undefined) row.stage = data.stage;
  if (data.amount !== undefined) row.amount = data.amount;
  if (data.product !== undefined) row.product = data.product;
  if (data.estimatedClosingDate !== undefined) row.estimated_closing_date = data.estimatedClosingDate;
  if (data.appDate !== undefined) row.app_date = data.appDate;
  
  // Loan details
  if (data.loanNumber !== undefined) row.loan_number = data.loanNumber;
  if (data.lender !== undefined) row.lender = data.lender;
  if (data.loanAmount !== undefined) row.loan_amount = data.loanAmount;
  if (data.downPayment !== undefined) row.down_payment = data.downPayment;
  if (data.interestRate !== undefined) row.interest_rate = data.interestRate;
  if (data.loanTerm !== undefined) row.loan_term = data.loanTerm;
  
  // Tags
  if (data.tags !== undefined) row.tags = data.tags;
  
  // Flatten propertyAddress
  if (data.propertyAddress) {
    row.property_street = data.propertyAddress.street || null;
    row.property_city = data.propertyAddress.city || null;
    row.property_state = data.propertyAddress.state || null;
    row.property_zip = data.propertyAddress.zip || null;
  }
  if (data.propertyType !== undefined) row.property_type = data.propertyType;
  if (data.occupancyType !== undefined) row.occupancy_type = data.occupancyType;

  // Flatten buyerAgent
  if (data.buyerAgent) {
    row.buyer_agent_name = data.buyerAgent.name || null;
    row.buyer_agent_email = data.buyerAgent.email || null;
    row.buyer_agent_phone = data.buyerAgent.phone || null;
    row.buyer_agent_company = data.buyerAgent.company || null;
  }
  
  // Flatten sellerAgent
  if (data.sellerAgent) {
    row.seller_agent_name = data.sellerAgent.name || null;
    row.seller_agent_email = data.sellerAgent.email || null;
    row.seller_agent_phone = data.sellerAgent.phone || null;
    row.seller_agent_company = data.sellerAgent.company || null;
  }
  
  // Flatten titleCompany
  if (data.titleCompany) {
    row.title_company_name = data.titleCompany.name || null;
    row.title_company_contact = data.titleCompany.contact || null;
    row.title_company_email = data.titleCompany.email || null;
    row.title_company_phone = data.titleCompany.phone || null;
  }
  
  if (data.loanPurpose !== undefined) row.loan_purpose = data.loanPurpose;
  if (data.purchaseOrRefi !== undefined) row.purchase_or_refi = data.purchaseOrRefi;
  if (data.creditScore !== undefined) row.credit_score = data.creditScore;
  
  // Flatten sourceDetails
  if (data.sourceDetails) {
    row.source_campaign = data.sourceDetails.campaignId || null;
    row.source_medium = data.sourceDetails.medium || null;
    row.source_term = data.sourceDetails.term || null;
  }
  if (data.source !== undefined) row.source_medium = data.source;
  if (data.formName !== undefined) row.form_name = data.formName;
  
  if (data.nextAction !== undefined) row.next_action = data.nextAction;
  if (data.nextActionDue !== undefined) row.next_action_due = data.nextActionDue;
  if (data.isAtRisk !== undefined) row.is_at_risk = data.isAtRisk;
  if (data.ariveId !== undefined) row.arive_id = data.ariveId;
  if (data.ariveSysGuid !== undefined) row.arive_sys_guid = data.ariveSysGuid;
  if (data.ariveDeepLink !== undefined) row.arive_deep_link = data.ariveDeepLink;
  if (data.ariveEntityType !== undefined) row.arive_entity_type = data.ariveEntityType;
  if (data.ariveLastSyncedAt !== undefined) row.arive_last_synced_at = data.ariveLastSyncedAt;
  if (data.ariveLoanData !== undefined) row.arive_loan_data = data.ariveLoanData;
  if (data.slaTimestamp !== undefined) row.sla_timestamp = data.slaTimestamp;
  if (data.extraFields !== undefined) row.extra_fields = data.extraFields || null;
  
  return row;
}

/**
 * Transform snake_case task rows to camelCase for frontend compatibility
 */
export function transformTaskToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    type: row.type,
    status: row.status,
    priority: row.priority,
    dueDate: row.due_date ? new Date(row.due_date) : null,
    dueTime: row.due_time || null, // HH:mm ET; null = all-day
    eventDuration: row.event_duration_minutes || null,
    assignedTo: row.assigned_to,
    createdBy: row.created_by,
    completedAt: row.completed_at ? new Date(row.completed_at) : null,
    // Related entity info (flattened)
    relatedTo: row.related_entity_type ? {
      type: row.related_entity_type,
      id: row.related_entity_id,
      name: row.related_entity_name,
    } : null,
    contactId: row.contact_id,
    dealId: row.deal_id,
    // Calendar integration
    isCalendarEvent: row.is_calendar_event,
    googleCalendarEventId: row.google_calendar_event_id,
    googleSyncStatus: row.google_sync_status || null,
    // Metadata
    source: row.source,
    tags: row.tags || [],
    createdAt: row.created_at ? new Date(row.created_at) : null,
    updatedAt: row.updated_at ? new Date(row.updated_at) : null,
    // Joined relations (present when the query selects crm_contacts/crm_deals)
    contact: row.crm_contacts ? {
      id: row.crm_contacts.id,
      firstName: row.crm_contacts.first_name,
      lastName: row.crm_contacts.last_name,
      email: row.crm_contacts.email,
    } : null,
    deal: row.crm_deals ? {
      id: row.crm_deals.id,
      dealName: row.crm_deals.deal_name,
      stage: row.crm_deals.stage,
    } : null,
  };
}

/**
 * Transform camelCase task data to snake_case for Supabase inserts/updates
 */
export function transformTaskToSnakeCase(data) {
  const row = {};
  
  if (data.title !== undefined) row.title = data.title;
  if (data.description !== undefined) row.description = data.description;
  if (data.type !== undefined) row.type = data.type;
  if (data.status !== undefined) row.status = data.status;
  if (data.priority !== undefined) row.priority = data.priority;
  if (data.assignedTo !== undefined) row.assigned_to = data.assignedTo;
  if (data.createdBy !== undefined) row.created_by = data.createdBy;
  
  // Handle date conversion
  if (data.dueDate !== undefined) {
    row.due_date = data.dueDate instanceof Date 
      ? data.dueDate.toISOString() 
      : data.dueDate;
  }
  if (data.dueTime !== undefined) row.due_time = data.dueTime || null;
  if (data.eventDuration !== undefined) {
    row.event_duration_minutes = data.eventDuration ? parseInt(data.eventDuration, 10) : null;
  }
  if (data.completedAt !== undefined) {
    row.completed_at = data.completedAt instanceof Date 
      ? data.completedAt.toISOString() 
      : data.completedAt;
  }
  
  // Handle relatedTo object (flatten)
  if (data.relatedTo) {
    row.related_entity_type = data.relatedTo.type || null;
    row.related_entity_id = data.relatedTo.id || null;
    row.related_entity_name = data.relatedTo.name || null;
  }

  if (data.contactId !== undefined) row.contact_id = data.contactId;
  if (data.dealId !== undefined) row.deal_id = data.dealId;

  // Derive the FK columns from relatedTo when the caller didn't set them
  // explicitly. The crm_get_user_tasks RPC joins (and deal-ownership
  // visibility) run on contact_id/deal_id, so tasks created with only a
  // relatedTo link used to be invisible to those joins.
  if (data.relatedTo?.id) {
    if (data.relatedTo.type === 'contact' && data.contactId === undefined) {
      row.contact_id = data.relatedTo.id;
    }
    if (data.relatedTo.type === 'deal' && data.dealId === undefined) {
      row.deal_id = data.relatedTo.id;
    }
  }

  if (data.source !== undefined) row.source = data.source;
  if (data.tags !== undefined) row.tags = data.tags;
  if (data.isCalendarEvent !== undefined) row.is_calendar_event = data.isCalendarEvent;
  if (data.googleCalendarEventId !== undefined) row.google_calendar_event_id = data.googleCalendarEventId;

  return row;
}

/**
 * Transform snake_case crm_calendar_events rows to the camelCase event shape
 * the calendar UI has always used (startTime/endTime as JS Dates).
 */
export function transformCalendarEventToCamelCase(row) {
  if (!row) return null;

  return {
    id: row.id,
    userId: row.user_id,
    title: row.title,
    description: row.description,
    eventType: row.event_type || 'general',
    startTime: row.start_at ? new Date(row.start_at) : null,
    endTime: row.end_at ? new Date(row.end_at) : null,
    allDay: row.all_day || false,
    attendees: row.attendees || [],
    addVideoLink: row.add_video_link || false,
    googleMeetLink: row.google_meet_link || null,
    zoomMeetingLink: row.zoom_link || null,
    relatedTo: row.related_entity_type ? {
      type: row.related_entity_type,
      id: row.related_entity_id,
      name: row.related_entity_name,
    } : null,
    dealId: row.deal_id,
    contactId: row.contact_id,
    origin: row.origin,
    googleCalendarEventId: row.google_event_id || null,
    googleHtmlLink: row.google_html_link || null,
    syncStatus: row.sync_status,
    syncError: row.sync_error || null,
    syncedWithGoogle: row.sync_status === 'synced' && Boolean(row.google_event_id),
    deletedAt: row.deleted_at || null,
    createdAt: row.created_at ? new Date(row.created_at) : null,
    updatedAt: row.updated_at ? new Date(row.updated_at) : null,
  };
}

/**
 * Transform snake_case activity rows to camelCase for frontend compatibility
 */
export function transformActivityToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    dealId: row.deal_id,
    authorId: row.author_id,
    type: row.type,
    content: row.content,
    text: row.content, // Alias for backward compatibility
    timestamp: row.created_at,
    createdAt: row.created_at,
    // Spread metadata for backward compatibility
    ...(row.metadata || {}),
  };
}

/**
 * Transform camelCase activity data to snake_case for Supabase inserts
 */
export function transformActivityToSnakeCase(data) {
  const row = {};
  
  if (data.dealId !== undefined) row.deal_id = data.dealId;
  if (data.authorId !== undefined) row.author_id = data.authorId;
  if (data.type !== undefined) row.type = data.type;
  if (data.content !== undefined) row.content = data.content;
  if (data.text !== undefined) row.content = data.text;
  
  // Gather metadata
  const metadataKeys = ['authorName', 'authorPhotoURL', 'disposition', 'changes', 
                        'previousStage', 'newStage', 'duration', 'dialpadCallId', 'hasTranscript'];
  const metadata = {};
  metadataKeys.forEach(key => {
    if (data[key] !== undefined) metadata[key] = data[key];
  });
  if (Object.keys(metadata).length > 0) {
    row.metadata = metadata;
  }
  
  return row;
}

/**
 * Transform snake_case audit log rows to camelCase for frontend
 */
export function transformAuditLogToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    action: row.action,
    userId: row.user_id,
    userName: row.user_name,
    entityType: row.entity_type,
    entityId: row.entity_id,
    details: row.details || {},
    beforeSnapshot: row.before_snapshot,
    afterSnapshot: row.after_snapshot,
    createdAt: row.created_at,
    timestamp: row.created_at,
  };
}

/**
 * Transform snake_case import history rows to camelCase for frontend
 */
export function transformImportHistoryToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    userId: row.user_id,
    userName: row.user_name,
    fileName: row.file_name,
    totalRows: row.total_rows,
    importedRows: row.imported_rows,
    updatedRows: row.updated_rows || 0,
    failedRows: row.failed_rows,
    duplicatesFound: row.duplicates_found,
    errors: row.errors || [],
    status: row.status,
    completedAt: row.completed_at,
    createdAt: row.created_at,
  };
}

/**
 * Transform snake_case dialpad call rows to camelCase for frontend
 */
export function transformDialpadCallToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    contactId: row.contact_id,
    dealId: row.deal_id,
    userId: row.user_id,
    dialpadCallId: row.dialpad_call_id,
    direction: row.direction,
    duration: row.duration,
    status: row.status,
    recordingUrl: row.recording_url,
    transcript: row.transcript,
    fromNumber: row.from_number,
    toNumber: row.to_number,
    startedAt: row.started_at,
    endedAt: row.ended_at,
    createdAt: row.created_at,
    timestamp: row.started_at || row.created_at,
    ...(row.metadata || {}),
  };
}

/**
 * Transform snake_case dialpad SMS rows to camelCase for frontend
 */
export function transformDialpadSmsToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    contactId: row.contact_id,
    dealId: row.deal_id,
    userId: row.user_id,
    dialpadMessageId: row.dialpad_message_id,
    direction: row.direction,
    content: row.content,
    text: row.content, // Alias
    fromNumber: row.from_number,
    toNumber: row.to_number,
    status: row.status,
    sentAt: row.sent_at,
    createdAt: row.created_at,
    timestamp: row.sent_at || row.created_at,
    ...(row.metadata || {}),
  };
}

/**
 * Transform snake_case deal participant rows to camelCase for frontend
 */
export function transformParticipantToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    dealId: row.deal_id,
    contactId: row.contact_id,
    role: row.role,
    isPrimary: row.is_primary,
    createdAt: row.created_at,
    // Include joined contact data if present
    contact: row.crm_contacts ? transformContactToCamelCase(row.crm_contacts) : null,
  };
}

/**
 * Fetch all rows from a Supabase query using pagination.
 * Supabase has a server-side max_rows limit (typically 1000) that cannot be overridden
 * by the client .limit() method. We must use .range() pagination to get all rows.
 *
 * A deterministic ORDER BY is mandatory: OFFSET paging without one lets Postgres
 * return rows in a different order per page, which silently duplicates some rows
 * and drops others. That matters most for "Select All" and CSV export, where the
 * result is expected to exactly match the table's total count.
 * 
 * @param {string} table - Table name
 * @param {string} selectFields - Fields to select
 * @param {Function} filterFn - Function that receives query and applies filters, returns modified query
 * @param {number} pageSize - Page size for pagination (default 1000)
 * @param {string} orderColumn - Unique column to sort by for stable paging (default 'id')
 * @returns {Promise<Array>} All rows
 * 
 * @example
 * const deals = await fetchAllRows('crm_deals', 'id, stage, is_at_risk', (query) => {
 *   return query.eq('owner_id', userId);
 * });
 */
export async function fetchAllRows(table, selectFields, filterFn = null, pageSize = 1000, orderColumn = 'id') {
  if (!supabase) {
    throw new Error('Supabase is not configured');
  }
  
  let allRows = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase.from(table).select(selectFields);
    if (filterFn) {
      query = filterFn(query);
    }
    query = query
      .order(orderColumn, { ascending: true })
      .range(offset, offset + pageSize - 1);
    
    const { data, error } = await query;
    if (error) throw error;
    
    if (!data || data.length === 0) {
      hasMore = false;
    } else {
      allRows = allRows.concat(data);
      // Advance by rows actually received, so a short page can never skip rows
      offset += data.length;
      // If we got fewer rows than requested, we've reached the end
      if (data.length < pageSize) {
        hasMore = false;
      }
    }
  }
  
  return allRows;
}

/**
 * Transform snake_case hot lead queue rows to camelCase for frontend
 */
export function transformHotLeadToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    batchId: row.batch_id,
    dealScore: row.deal_score,
    dealSummary: row.deal_summary,
    recommendedAction: row.recommended_action,
    estimatedMonthlySavings: row.estimated_monthly_savings,
    primaryOpportunity: row.primary_opportunity,
    leadData: row.lead_data,
    status: row.status,
    claimedBy: row.claimed_by,
    claimedAt: row.claimed_at,
    convertedDealId: row.converted_deal_id,
    convertedContactId: row.converted_contact_id,
    recycleCount: row.recycle_count,
    lastRecycledAt: row.last_recycled_at,
    lastRecycledFrom: row.last_recycled_from,
    existingContactId: row.existing_contact_id,
    duplicateCheckResult: row.duplicate_check_result,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    // Extract commonly displayed fields from lead_data for convenience
    borrowerName: row.lead_data?.borrowerName,
    borrowerState: row.lead_data?.borrowerState,
    borrowerCity: row.lead_data?.borrowerCity,
    mortgageType: row.lead_data?.mortgageType,
    currentBalance: parseFloat(row.lead_data?.currentBalance) || null,
    interestRate: parseFloat(row.lead_data?.interestRate) || null,
    loanSummary: row.lead_data?.loanSummary || row.deal_summary,
  };
}

export default supabase;

